/**
 * Add-on quantity confirmation dialog - SHARED between web_account (BillingSection) and
 * web_admin (PlanPage); both used to have their own ad-hoc ConfirmDialog that never said
 * what a removal actually costs.
 *
 * Two things it adds:
 *  1. **Impact** - which limits fall below current usage (storage, AI quotas) and which
 *     features switch off (e.g. `sections.accountShare`, which "+1 site" turns on).
 *     Advisory - the server does not block on it.
 *  2. **Site choice** - when `keepRequired`, the removal would leave sites outside the
 *     quota. The server refuses such a change without a choice (409 `sites_over_quota`),
 *     so the enforcement cron never decides for the customer which site goes dark.
 */
import { AlertTriangle, Loader2 } from "lucide-react";
import { Button } from "../components/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/dialog";
import { makeT, type T } from "./i18n";
import type { ChangeImpacts, Locale, QuotaOptionSite } from "./types";
import { ImpactList } from "./change-dialog/ImpactList";
import { SitePicker } from "./change-dialog/SitePicker";
import { hasImpacts } from "./change-dialog/lib";

/** `POST /api/billing/add-ons/preview` response. */
export interface AddOnChangePreviewData {
  impacts: ChangeImpacts;
  quota: { activeCount: number; allowed: number; deficit: number; over: boolean };
  /** The site quota would be exceeded -> the choice is mandatory (the server enforces it). */
  keepRequired: boolean;
  sites: QuotaOptionSite[];
}

export interface AddOnChangeDialogProps {
  open: boolean;
  /** Add-on display name in the chosen locale. */
  addOnName: string;
  /** New quantity (0 = remove). */
  quantity: number;
  /** Price label for the new quantity, e.g. "18 EUR/month". Empty -> the price row is skipped. */
  priceLabel: string;
  /** Whether the new quantity is lower than the current one (credit) or higher (billed now). */
  decreasing: boolean;
  preview: AddOnChangePreviewData | null;
  loading: boolean;
  applying: boolean;
  keepIds: string[];
  onKeepChange: (ids: string[]) => void;
  locale: Locale;
  onConfirm: () => void;
  onCancel: () => void;
}

export function AddOnChangeDialog({
  open,
  addOnName,
  quantity,
  priceLabel,
  decreasing,
  preview,
  loading,
  applying,
  keepIds,
  onKeepChange,
  locale,
  onConfirm,
  onCancel,
}: AddOnChangeDialogProps) {
  const t: T = makeT(locale);
  const removing = quantity === 0;
  const keepRequired = Boolean(preview?.keepRequired);
  const capacity = preview?.quota.allowed ?? 0;
  // The server refuses without a choice, so keep the button disabled until one is made.
  const blocked = loading || (keepRequired && (keepIds.length === 0 || keepIds.length > capacity));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !applying) onCancel(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t(removing ? "addOn.confirmTitleRemove" : "addOn.confirmTitleAdd")}</DialogTitle>
          <DialogDescription>
            {addOnName}
            {!removing && quantity > 1 ? ` × ${quantity}` : ""}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] space-y-3 overflow-y-auto py-1 pr-0.5">
          {!removing && priceLabel && (
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm text-muted-foreground">{t("addOn.newPrice")}</span>
              <span className="text-lg font-semibold tabular-nums">{priceLabel}</span>
            </div>
          )}

          <p className="text-xs text-muted-foreground">{decreasing ? t("addOn.removeCredit") : t("addOn.addCharge")}</p>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> {t("confirm.loading")}
            </div>
          )}

          {preview && hasImpacts(preview.impacts) && (
            <ImpactList impacts={preview.impacts} locale={locale} t={t} omitKeys={["sites.max"]} />
          )}

          {preview && keepRequired && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                <p className="text-sm text-foreground">{t("addOn.keepRequired")}</p>
              </div>
              <div className="mt-3 border-t border-amber-500/20 pt-3">
                <SitePicker
                  sites={preview.sites}
                  selected={keepIds}
                  capacity={capacity}
                  onToggle={(siteId) =>
                    onKeepChange(
                      keepIds.includes(siteId)
                        ? keepIds.filter((id) => id !== siteId)
                        : keepIds.length >= capacity
                          ? keepIds
                          : [...keepIds, siteId],
                    )
                  }
                  t={t}
                  disabled={applying}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={applying}>{t("confirm.cancel")}</Button>
          <Button
            variant={removing ? "outline" : "primary"}
            onClick={onConfirm}
            loading={applying}
            disabled={blocked}
          >
            {t("confirm.confirm")}
          </Button>
        </DialogFooter>
        {keepRequired && keepIds.length === 0 && (
          <p className="text-right text-xs text-amber-700 dark:text-amber-400">{t("quota.pickRequired")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
